import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  BoxData,
  ResourcesContent,
  CollectedData,
  MainResponseData,
  LubavSessionType,
} from '@backrs_ui/grow-ui/src/models/models';

import { initiateSession } from '../coachLubav/coachLubav.thunk';
import { getSessionState } from '../lubavSessionState/lubavSessionState.utils';
import { handlePending, handleRejected } from '../reducers';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

type BoxDataState = Readonly<{
  loading: boolean;
  boxData: BoxData;
  currentResponseId?: number;
  error?: string;
}>;

export const initialState: BoxDataState = {
  loading: false,
  boxData: {
    mainResponse: {
      main_response: undefined,
      resources: undefined,
      final_message: undefined,
    },
    collectedData: {
      box_1: { name: [], value: [] },
      box_2: { name: [], value: [] },
      hidden: { name: [], value: [] },
    },
  },
};

export const boxDataSlice = createSlice({
  name: 'boxData',
  initialState,
  reducers: {
    setIsLoadingResources: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setBoxData: (state, action: PayloadAction<BoxData>) => {
      state.boxData = action.payload;
    },
    setMainResponseData: (state, action: PayloadAction<MainResponseData>) => {
      state.boxData.mainResponse = action.payload;
    },
    setResourcesContent: (state, action: PayloadAction<ResourcesContent | undefined>) => {
      if (action.payload) {
        state.boxData.mainResponse = {
          ...state.boxData.mainResponse,
          ...state.boxData.collectedData,
          resources: action.payload,
        };
      }
    },
    setCollectedData: (state, action: PayloadAction<CollectedData>) => {
      state.boxData.collectedData = action.payload;
    },
    setCurrentResponseId: (state, action: PayloadAction<number>) => {
      state.currentResponseId = action.payload;
    },
    resetBoxData: (state) => {
      state.boxData = initialState.boxData;
      state.loading = false;
      state.error = undefined;
    },
    resetMainResponse: (state) => {
      state.boxData.mainResponse.main_response = initialState.boxData.mainResponse.main_response;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateSession.pending, handlePending)
      .addCase(initiateSession.rejected, handleRejected)
      .addCase(initiateSession.fulfilled, (state) => {
        state.boxData = initialState.boxData;
        state.loading = false;
        state.error = undefined;
      })

      // LOGOUT
      .addCase(logout, (state) => {
        state.boxData = initialState.boxData;
        state.loading = false;
        state.error = undefined;
      });
  },
});

export const {
  setBoxData,
  resetBoxData,
  setResourcesContent,
  setCollectedData,
  setMainResponseData,
  setIsLoadingResources,
  setCurrentResponseId,
  resetMainResponse,
} = boxDataSlice.actions;

// Reducer
export const boxDataReducer = boxDataSlice.reducer;

export const selectResourcesContent = (state: RootState, sessionType: LubavSessionType | null) =>
  getSessionState(state, sessionType)?.boxData?.boxData?.mainResponse?.resources;
